import { useState } from 'react';
import { formatMoney } from '@/lib/money';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-provider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from '@/components/ui/table';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Users,
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  ArrowLeft,
  Loader2,
  Mail,
  Phone,
  Building,
  TrendingUp,
  UserPlus,
  Upload,
  Download,
  Filter,
} from 'lucide-react';

// ── Types ──

interface Contact {
  _id: string;
  email: string;
  name: string;
  phone?: string;
  company?: string;
  source: 'contact-form' | 'newsletter' | 'order' | 'manual' | 'import' | 'chatbot';
  status: 'lead' | 'prospect' | 'customer' | 'churned';
  tags: string[];
  customFields?: Record<string, unknown>;
  score: number;
  assignedTo?: string;
  totalOrders?: number;
  totalRevenue?: number;
  lastContactedAt?: string;
  lastActivityAt?: string;
  notes?: Array<{ text: string; createdBy?: string; createdAt?: string }>;
  createdAt: string;
  updatedAt: string;
}

interface DashboardData {
  totalContacts: number;
  contactsByStatus: Record<string, number>;
  newContactsThisMonth: number;
  totalDeals: number;
  openDealsValue: number;
  wonDealsValue: number;
  topSources: Array<{ source: string; count: number }>;
  recentActivity: Array<{ type: string; description: string; createdAt: string }>;
}

interface ContactFormData {
  email: string;
  name: string;
  phone: string;
  company: string;
  source: Contact['source'];
  status: Contact['status'];
  score: number;
  tags: string;
}

const EMPTY_FORM: ContactFormData = {
  email: '',
  name: '',
  phone: '',
  company: '',
  source: 'manual',
  status: 'lead',
  score: 50,
  tags: '',
};

const SOURCES: Contact['source'][] = [
  'contact-form',
  'newsletter',
  'order',
  'manual',
  'import',
  'chatbot',
];

const STATUSES: Contact['status'][] = ['lead', 'prospect', 'customer', 'churned'];

// ── Helpers ──

function statusColor(status: string): string {
  switch (status) {
    case 'lead':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'prospect':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'customer':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'churned':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
}

function sourceColor(source: string): string {
  switch (source) {
    case 'contact-form':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'newsletter':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    case 'order':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'manual':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    case 'import':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    case 'chatbot':
      return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
}

function formatCents(cents: number): string {
  // Delegate to the single money formatter (cents are minor units, USD).
  return formatMoney(cents, 'USD', { alwaysDecimals: true });
}

function scoreBar(score: number) {
  let color = 'bg-red-500';
  if (score > 70) color = 'bg-green-500';
  else if (score >= 40) color = 'bg-yellow-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-muted-foreground text-xs">{score}</span>
    </div>
  );
}

// ── Component ──

export default function CRM() {
  const { projectId } = useParams<{ projectId: string }>();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Filters & pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Dialogs
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [form, setForm] = useState<ContactFormData>(EMPTY_FORM);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [activeTab, setActiveTab] = useState<'contacts' | 'deals'>('contacts');

  // ── Queries ──

  const basePath = `/projects/${projectId}/crm`;

  const {
    data: contactsRes,
    isLoading: contactsLoading,
    isError: contactsError,
  } = useQuery({
    queryKey: ['crm-contacts', projectId, page, statusFilter, sourceFilter, search],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (search) params.search = search;
      const res = await api.get(`${basePath}/contacts`, { params });
      return res.data;
    },
    enabled: Boolean(projectId),
  });

  const { data: dashboardRes } = useQuery({
    queryKey: ['crm-dashboard', projectId],
    queryFn: async () => {
      const res = await api.get(`${basePath}/dashboard`);
      return res.data;
    },
    enabled: Boolean(projectId),
  });

  const contacts: Contact[] = contactsRes?.data || [];
  const meta = contactsRes?.meta || { total: 0, page: 1, totalPages: 1 };
  const dashboard: DashboardData | null = dashboardRes?.data || null;

  // ── Mutations ──

  const createMutation = useMutation({
    mutationFn: async (data: Partial<Contact>) => {
      await api.post(`${basePath}/contacts`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-contacts', projectId] });
      queryClient.invalidateQueries({ queryKey: ['crm-dashboard', projectId] });
      toast.success('Contact created');
      closeContactDialog();
    },
    onError: () => {
      toast.error('Failed to create contact');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Contact> }) => {
      await api.put(`${basePath}/contacts/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-contacts', projectId] });
      queryClient.invalidateQueries({ queryKey: ['crm-dashboard', projectId] });
      toast.success('Contact updated');
      closeContactDialog();
    },
    onError: () => {
      toast.error('Failed to update contact');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${basePath}/contacts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-contacts', projectId] });
      queryClient.invalidateQueries({ queryKey: ['crm-dashboard', projectId] });
      toast.success('Contact deleted');
    },
    onError: () => {
      toast.error('Failed to delete contact');
    },
  });

  const importMutation = useMutation({
    mutationFn: async (contacts: unknown[]) => {
      await api.post(`${basePath}/import`, { contacts, skipDuplicates: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-contacts', projectId] });
      queryClient.invalidateQueries({ queryKey: ['crm-dashboard', projectId] });
      toast.success('Contacts imported');
      setImportDialogOpen(false);
      setImportJson('');
    },
    onError: () => {
      toast.error('Failed to import contacts');
    },
  });

  // ── Handlers ──

  function openCreateDialog() {
    setEditingContact(null);
    setForm(EMPTY_FORM);
    setContactDialogOpen(true);
  }

  function openEditDialog(contact: Contact) {
    setEditingContact(contact);
    setForm({
      email: contact.email,
      name: contact.name,
      phone: contact.phone || '',
      company: contact.company || '',
      source: contact.source,
      status: contact.status,
      score: contact.score,
      tags: (contact.tags || []).join(', '),
    });
    setContactDialogOpen(true);
  }

  function closeContactDialog() {
    setContactDialogOpen(false);
    setEditingContact(null);
    setForm(EMPTY_FORM);
  }

  function handleSave() {
    if (!form.email.trim() || !form.name.trim()) {
      toast.error('Email and name are required');
      return;
    }
    const payload: Partial<Contact> = {
      email: form.email.trim(),
      name: form.name.trim(),
      phone: form.phone.trim() || undefined,
      company: form.company.trim() || undefined,
      source: form.source,
      status: form.status,
      score: form.score,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    };
    if (editingContact) {
      updateMutation.mutate({ id: editingContact._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function handleImport() {
    try {
      const parsed = JSON.parse(importJson);
      if (!Array.isArray(parsed)) {
        toast.error('JSON must be an array of contact objects');
        return;
      }
      importMutation.mutate(parsed);
    } catch {
      toast.error('Invalid JSON');
    }
  }

  async function handleExport() {
    try {
      const res = await api.get(`${basePath}/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm-contacts-${projectId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Contacts exported');
    } catch {
      toast.error('Failed to export contacts');
    }
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
  }

  // ── Render ──

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/projects/${projectId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Users className="h-6 w-6" />
              CRM
            </h1>
            <p className="text-muted-foreground text-sm">
              Manage contacts, track leads, and monitor deals
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={activeTab === 'contacts' ? 'default' : 'outline'}
            onClick={() => setActiveTab('contacts')}
          >
            <Users className="mr-2 h-4 w-4" />
            Contacts
          </Button>
          <Button
            variant={activeTab === 'deals' ? 'default' : 'outline'}
            onClick={() => {
              setActiveTab('deals');
              navigate(`/projects/${projectId}/crm/pipeline`);
            }}
          >
            <TrendingUp className="mr-2 h-4 w-4" />
            Deals
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Contacts</CardTitle>
            <Users className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dashboard?.totalContacts?.toLocaleString() ?? '-'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">New This Month</CardTitle>
            <UserPlus className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dashboard?.newContactsThisMonth?.toLocaleString() ?? '-'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Open Deals Value</CardTitle>
            <TrendingUp className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dashboard ? formatCents(dashboard.openDealsValue) : '-'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Won Deals Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {dashboard ? formatCents(dashboard.wonDealsValue) : '-'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <form onSubmit={handleSearchSubmit} className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </form>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <Filter className="mr-1 h-3 w-3" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={sourceFilter}
            onValueChange={(v) => {
              setSourceFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <Filter className="mr-1 h-3 w-3" />
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {SOURCES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add Contact
          </Button>
          <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Import
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Contacts Table */}
      <Card>
        <CardContent className="p-0">
          {contactsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
              <span className="text-muted-foreground ml-2">Loading contacts...</span>
            </div>
          ) : contactsError ? (
            <div className="text-muted-foreground flex flex-col items-center justify-center py-16">
              <p>Failed to load contacts.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() =>
                  queryClient.invalidateQueries({ queryKey: ['crm-contacts', projectId] })
                }
              >
                Retry
              </Button>
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-muted-foreground flex flex-col items-center justify-center py-16">
              <Users className="mb-3 h-10 w-10" />
              <p className="font-medium">No contacts found</p>
              <p className="mt-1 text-sm">
                {search || statusFilter !== 'all' || sourceFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Add your first contact to get started'}
              </p>
              {!search && statusFilter === 'all' && sourceFilter === 'all' && (
                <Button className="mt-4" onClick={openCreateDialog}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Contact
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact._id}>
                    <TableCell>
                      <div>
                        <Link
                          to={`/projects/${projectId}/crm/contacts/${contact._id}`}
                          className="font-medium hover:underline"
                        >
                          {contact.name}
                        </Link>
                        <div className="text-muted-foreground flex items-center gap-1 text-xs">
                          <Mail className="h-3 w-3" />
                          {contact.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {contact.company ? (
                        <span className="flex items-center gap-1 text-sm">
                          <Building className="text-muted-foreground h-3 w-3" />
                          {contact.company}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.phone ? (
                        <span className="flex items-center gap-1 text-sm">
                          <Phone className="text-muted-foreground h-3 w-3" />
                          {contact.phone}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={sourceColor(contact.source)}>
                        {contact.source.replace('-', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={statusColor(contact.status)}>
                        {contact.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{scoreBar(contact.score)}</TableCell>
                    <TableCell>
                      <div className="flex max-w-[150px] flex-wrap gap-1">
                        {(contact.tags || []).slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="outline" className="px-1.5 py-0 text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {(contact.tags || []).length > 3 && (
                          <Badge variant="outline" className="px-1.5 py-0 text-xs">
                            +{contact.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(contact.createdAt)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(`/projects/${projectId}/crm/contacts/${contact._id}`)
                            }
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Detail
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(contact)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={async () => {
                              if (
                                await confirm({
                                  title: 'Delete contact?',
                                  message: 'Delete this contact?',
                                  danger: true,
                                })
                              ) {
                                deleteMutation.mutate(contact._id);
                              }
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            Page {meta.page} of {meta.totalPages} ({meta.total} contacts)
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= meta.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create / Edit Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="crm-name">Name *</Label>
                <Input
                  id="crm-name"
                  placeholder="John Doe"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="crm-email">Email *</Label>
                <Input
                  id="crm-email"
                  type="email"
                  placeholder="john@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="crm-phone">Phone</Label>
                <Input
                  id="crm-phone"
                  placeholder="+1 555-0123"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="crm-company">Company</Label>
                <Input
                  id="crm-company"
                  placeholder="Acme Inc"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Source</Label>
                <Select
                  value={form.source}
                  onValueChange={(v) => setForm({ ...form, source: v as Contact['source'] })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v as Contact['status'] })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="crm-score">Score (0-100)</Label>
                <Input
                  id="crm-score"
                  type="number"
                  min={0}
                  max={100}
                  value={form.score}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      score: Math.min(100, Math.max(0, Number(e.target.value) || 0)),
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="crm-tags">Tags (comma-separated)</Label>
                <Input
                  id="crm-tags"
                  placeholder="vip, enterprise"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeContactDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editingContact ? 'Save Changes' : 'Create Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Contacts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-muted-foreground text-sm">
              Paste a JSON array of contact objects below. Each object should include at minimum{' '}
              <code className="bg-muted rounded px-1 py-0.5 text-xs">email</code> and{' '}
              <code className="bg-muted rounded px-1 py-0.5 text-xs">name</code> fields. Duplicate
              emails will be skipped.
            </p>
            <textarea
              className="bg-background focus:ring-ring min-h-[200px] w-full resize-y rounded-md border p-3 font-mono text-sm focus:outline-none focus:ring-2"
              placeholder={`[
  { "email": "alice@example.com", "name": "Alice", "source": "import" },
  { "email": "bob@example.com", "name": "Bob", "source": "import" }
]`}
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={importMutation.isPending || !importJson.trim()}
            >
              {importMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
